import React, { useEffect, useState } from 'react';
import { supabase } from '../config/supabase';
import Layout from '../components/Layout';
import { useProfile } from '../contexts/ProfileContext';
import { format } from 'date-fns';

interface DatabaseNovedadResponse {
  id: string;
  empleado_id: string;
  tipo_novedad_id: string;
  observacion: string | null;
  fecha_hora_inicio: string;
  fecha_hora_fin: string | null;
  estado: string | null;
  created_at: string;
  tipo_novedad: {
    nombre: string;
  };
  empleado: {
    id: string;
    name: string;
    email: string;
    role: string;
    is_active: boolean | null;
    last_activity: string | null;
    created_at: string;
    updated_at: string;
    created_by: string;
    updated_by: string;
  };
}

interface TipoNovedad {
  nombre: string;
}

interface Profile {
  id: string;
  name: string;
  email: string;
  role: string;
  is_active: boolean | null;
  last_activity: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

interface Novedad {
  id: string;
  empleado_id: string;
  tipo_novedad_id: string;
  observacion: string | null;
  fecha_hora_inicio: string;
  fecha_hora_fin: string | null;
  estado: string | null;
  created_at: string;
  tipo_novedad: TipoNovedad;
  empleado: Profile;
}

const ReporteNovedades = () => {
  const { profile } = useProfile();
  const [novedades, setNovedades] = useState<Novedad[]>([]);
  const [empleados, setEmpleados] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({
    empleadoId: '',
    fecha: format(new Date(), 'yyyy-MM-dd'),
    estado: ''
  });

  // Cargar lista de empleados
  useEffect(() => {
    const fetchEmpleados = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('role', 'employee');

        if (error) throw error;
        if (data) setEmpleados(data);
      } catch (error) {
        console.error('Error al cargar empleados:', error);
      }
    };

    if (profile?.role === 'supervisor') {
      fetchEmpleados();
    }
  }, [profile]);

  // Cargar novedades con filtros
  useEffect(() => {
    const fetchNovedades = async () => {
      try {
        setLoading(true);
        let query = supabase
          .from('registro_novedad')
          .select(`
            id,
            empleado_id,
            tipo_novedad_id,
            observacion,
            fecha_hora_inicio,
            fecha_hora_fin,
            estado,
            created_at,
            tipo_novedad!inner (
              nombre
            ),
            empleado:empleado_id!inner (
              id,
              name,
              email,
              role,
              is_active,
              last_activity,
              created_at,
              updated_at,
              created_by,
              updated_by
            )
          `);

        // Aplicar filtros
        if (filtros.empleadoId) {
          query = query.eq('empleado_id', filtros.empleadoId);
        }
        if (filtros.fecha) {
          query = query
            .gte('fecha_hora_inicio', `${filtros.fecha}T00:00:00`)
            .lte('fecha_hora_inicio', `${filtros.fecha}T23:59:59`);
        }
        if (filtros.estado) {
          query = query.eq('estado', filtros.estado);
        }

        const { data, error } = await query.order('fecha_hora_inicio', { ascending: false });

        if (error) throw error;
        if (data) {
          const novedadesFormateadas: Novedad[] = data.map((item: any) => {
            // Asegurarnos de que tipo_novedad y empleado sean objetos únicos
            const tipo_novedad = Array.isArray(item.tipo_novedad) 
              ? item.tipo_novedad[0] 
              : item.tipo_novedad;
            
            const empleado = Array.isArray(item.empleado)
              ? item.empleado[0]
              : item.empleado;

            return {
              id: item.id,
              empleado_id: item.empleado_id,
              tipo_novedad_id: item.tipo_novedad_id,
              observacion: item.observacion,
              fecha_hora_inicio: item.fecha_hora_inicio,
              fecha_hora_fin: item.fecha_hora_fin,
              estado: item.estado,
              created_at: item.created_at,
              tipo_novedad: {
                nombre: tipo_novedad.nombre
              },
              empleado: {
                id: empleado.id,
                name: empleado.name,
                email: empleado.email,
                role: empleado.role,
                is_active: empleado.is_active,
                last_activity: empleado.last_activity,
                created_at: empleado.created_at,
                updated_at: empleado.updated_at,
                created_by: empleado.created_by,
                updated_by: empleado.updated_by
              }
            };
          });
          setNovedades(novedadesFormateadas);
        }
      } catch (error) {
        console.error('Error al cargar novedades:', error);
      } finally {
        setLoading(false);
      }
    };

    if (profile?.role === 'supervisor') {
      fetchNovedades();
    }
  }, [filtros, profile]);

  const formatearFecha = (fecha: string) => {
    return format(new Date(fecha), 'dd/MM/yyyy HH:mm');
  };

  if (profile?.role !== 'supervisor') {
    return (
      <Layout>
        <div className="p-6">
          <p className="text-red-500">No tienes acceso a esta página</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Reporte de Novedades</h1>

        {/* Filtros */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="empleado" className="block text-sm font-medium text-gray-700 mb-1">
                Empleado
              </label>
              <select
                id="empleado"
                value={filtros.empleadoId}
                onChange={(e) => setFiltros({ ...filtros, empleadoId: e.target.value })}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="">Todos los empleados</option>
                {empleados.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="fecha" className="block text-sm font-medium text-gray-700 mb-1">
                Fecha
              </label>
              <input
                type="date"
                id="fecha"
                value={filtros.fecha}
                onChange={(e) => setFiltros({ ...filtros, fecha: e.target.value })}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="estado" className="block text-sm font-medium text-gray-700 mb-1">
                Estado
              </label>
              <select
                id="estado"
                value={filtros.estado}
                onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="">Todos los estados</option>
                <option value="En curso">En curso</option>
                <option value="Finalizada">Finalizada</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tabla de novedades */}
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
          </div>
        ) : novedades.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
            No hay novedades que coincidan con los filtros seleccionados
          </div>
        ) : (
          <div className="bg-white shadow-sm rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Empleado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Inicio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fin
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Observación
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {novedades.map((novedad) => (
                    <tr key={novedad.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{novedad.empleado.name}</div>
                        <div className="text-sm text-gray-500">{novedad.empleado.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{novedad.tipo_novedad.nombre}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            novedad.estado === 'En curso'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {novedad.estado || 'Sin estado'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {format(new Date(novedad.fecha_hora_inicio), 'dd/MM/yyyy HH:mm')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {novedad.fecha_hora_fin
                          ? format(new Date(novedad.fecha_hora_fin), 'dd/MM/yyyy HH:mm')
                          : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                        {novedad.observacion || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ReporteNovedades;
